import Icon from '@atoms/Icon/Icon';
import { IAccountDropdown } from '@models/IAccountDropdown';
import { IMainNavigationItem } from '@models/IHeader';
import ILanguageSelector from '@models/ILanguageSelector';
import { ILink } from '@models/ILink';
import IconSizeEnum from '@models/enums/IconSizeEnum';
import AccountNavigation from '@molecules/AccountNavigation/AccountNavigation';
import updateDataLayer from '@utils/helpers/gtm';
import useIsInIFrame from '@utils/hooks/useIsInIFrame';
import { AnimatePresence, motion } from 'framer-motion';
import { Dispatch, SetStateAction } from 'react';
import MobileAccountLink from './MobileAccountLink';
import MobileLanguageSelector from './MobileLanguageSelector';
import MobileSecondLevel from './MobileSecondLevel';

interface Props {
  readonly accountDropdownNavigation: IAccountDropdown;
  readonly accountLink?: ILink;
  readonly accountsEnabled: boolean;
  readonly activeCtaTabIndex: number;
  readonly isSecondLevelOpen: boolean;
  readonly languageActiveIndex: number;
  readonly languageSelector?: ILanguageSelector;
  readonly mainNavigation: IMainNavigationItem[];
  readonly serviceAccountLink?: ILink;
  readonly showAccountIcon?: boolean;
  readonly onSetActiveCtaTabIndex: Dispatch<SetStateAction<number>>;
  readonly onSetIsSecondLevelOpen: Dispatch<SetStateAction<boolean>>;
}

const MobileTopLevel = ({
  accountDropdownNavigation: {
    userAuthenticated,
    loggedInMenuTitle,
    loggedOutMenuTitle,
    loginEndpoint,
    signOutCta,
    signOutEndpoint,
    navigationDropdown,
  },
  accountLink,
  accountsEnabled,
  activeCtaTabIndex,
  isSecondLevelOpen,
  languageActiveIndex,
  languageSelector,
  mainNavigation,
  serviceAccountLink,
  showAccountIcon,
  onSetIsSecondLevelOpen,
  onSetActiveCtaTabIndex,
}: Props) => {
  const isInIFrame = useIsInIFrame();

  const handleMainNavItemClick = (index: number, itemName: string) => {
    onSetActiveCtaTabIndex(index);
    onSetIsSecondLevelOpen(true);

    updateDataLayer({
      event: 'menuUsed',
      content_type: 'header_menu_level_1',
      main_category: itemName,
      sub_category: undefined,
      tertiary_category: undefined,
    });
  };

  return (
    <motion.div
      initial="opened"
      animate={isSecondLevelOpen ? 'closed' : 'opened'}
      variants={{
        closed: {
          x: '-100%',
          transition: {
            delay: 0.15,
            duration: 1.1,
            ease: [0.74, 0, 0.19, 1.02],
          },
        },
        opened: {
          x: '0%',
          transition: {
            delay: 0.35,
            duration: 0.63,
            ease: [0.74, 0, 0.19, 1.02],
          },
        },
      }}
      className="flex h-full min-w-[100%] flex-row items-start"
    >
      <motion.ul className="flex h-full w-full flex-col items-start justify-between overflow-y-auto">
        <div className="w-full">
          {mainNavigation.map(({ link, subMenuItems, title = '' }, index) =>
            link?.url ? (
              <div key={`level-1-nav-link--${link?.name}`} className="w-full">
                <motion.li
                  onClick={() => {
                    handleMainNavItemClick(index, link?.name || '');
                  }}
                  className="h5 mb-8 flex w-full cursor-pointer flex-row items-center gap-x-4 pr-5 pl-5 text-black-100"
                  data-testid={`CT-01-Header-Mobile-Top-Level-Category-Item-${title}`}
                >
                  <a
                    {...(isSecondLevelOpen ? { tabIndex: -1 } : {})}
                    href={link.url}
                    target={isInIFrame ? '_parent' : link.target}
                    data-testid={`CT-01-Header-Mobile-Top-Level-Category-Link-${title}`}
                  >
                    {link?.name}
                  </a>

                  <div className="ml-auto flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-yellow">
                    <Icon name="east" size={IconSizeEnum.sm} />
                  </div>
                </motion.li>
              </div>
            ) : (
              <div key={`level-1-nav-link--${title}`} className="w-full">
                <motion.li
                  onClick={() => {
                    handleMainNavItemClick(index, title);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      e.stopPropagation();
                      handleMainNavItemClick(index, title);
                    }
                  }}
                  className="h5 mb-8 flex w-full cursor-pointer flex-row items-center gap-x-4 pr-5 pl-5 text-black-100"
                  tabIndex={isSecondLevelOpen ? -1 : 0}
                  data-testid={`CT-01-Header-Mobile-Top-Level-Category-Title-${title}`}
                >
                  {title}

                  <div className="ml-auto flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-yellow">
                    <Icon name="east" size={IconSizeEnum.sm} />
                  </div>
                </motion.li>

                <AnimatePresence mode="wait">
                  {index === activeCtaTabIndex && (
                    <MobileSecondLevel
                      activeTab={
                        index === activeCtaTabIndex ? subMenuItems : []
                      }
                      mainNavItemTitle={title}
                    />
                  )}
                </AnimatePresence>
              </div>
            )
          )}
        </div>

        <div className="w-full items-end">
          {showAccountIcon &&
            accountsEnabled &&
            userAuthenticated &&
            loggedInMenuTitle && (
              <>
                <motion.button
                  {...(isSecondLevelOpen ? { tabIndex: -1 } : {})}
                  onClick={() => {
                    onSetActiveCtaTabIndex(mainNavigation.length + 1);
                    onSetIsSecondLevelOpen(true);
                  }}
                  className="h5 mb-8 flex w-full cursor-pointer flex-row items-center gap-x-4 pr-5 pl-5 text-black-100"
                >
                  <div className="flex items-center justify-center">
                    <Icon
                      name="account"
                      size={IconSizeEnum.lg}
                      className="mr-2 shrink-0"
                    />
                    <span className="text-left">{loggedInMenuTitle}</span>
                  </div>

                  <div className="ml-auto flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-yellow">
                    <Icon name="east" size={IconSizeEnum.sm} />
                  </div>
                </motion.button>

                <AnimatePresence mode="wait">
                  {mainNavigation.length + 1 === activeCtaTabIndex && (
                    <motion.div
                      className="absolute top-0 right-0 bottom-0 left-0 overflow-y-auto"
                      initial={{ x: '200%' }}
                      animate={{ x: '100%' }}
                      exit={{ x: '200%' }}
                      transition={{
                        delay: 0.15,
                        duration: 1.1,
                        ease: [0.74, 0, 0.19, 1.02],
                      }}
                    >
                      <div className="container w-full list-none text-black-100">
                        <AccountNavigation
                          items={navigationDropdown}
                          signOutCta={signOutCta}
                          signOutEndpoint={signOutEndpoint}
                        />
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </>
            )}

          {accountsEnabled &&
            !userAuthenticated &&
            loggedOutMenuTitle &&
            loginEndpoint && (
              <MobileAccountLink
                title={loggedOutMenuTitle}
                href={loginEndpoint}
                target={isInIFrame ? '_parent' : '_self'}
                isTabbingDisabled={isSecondLevelOpen}
              />
            )}

          {!accountsEnabled && accountLink?.url && accountLink.name && (
            <MobileAccountLink
              title={accountLink.name}
              href={accountLink.url}
              target={isInIFrame ? '_parent' : accountLink.target || '_self'}
              isTabbingDisabled={isSecondLevelOpen}
            />
          )}

          {serviceAccountLink?.url && serviceAccountLink.name && (
            <MobileAccountLink
              title={serviceAccountLink.name}
              href={serviceAccountLink.url}
              target={
                isInIFrame ? '_parent' : serviceAccountLink.target || '_blank'
              }
              icon={serviceAccountLink.linkIcon}
              isTabbingDisabled={isSecondLevelOpen}
            />
          )}

          {!!languageSelector && (
            <>
              <motion.button
                {...(isSecondLevelOpen ? { tabIndex: -1 } : {})}
                onClick={() => {
                  onSetActiveCtaTabIndex(mainNavigation.length + 2);
                  onSetIsSecondLevelOpen(true);
                }}
                className="h5 mb-8 flex w-full cursor-pointer flex-row items-center gap-x-4 pr-5 pl-5 text-black-100"
              >
                <div className="flex items-center justify-center">
                  <Icon
                    name="language"
                    size={IconSizeEnum.lg}
                    className="mr-2 shrink-0"
                  />
                  <span className="text-left">
                    {languageSelector.links[languageActiveIndex]?.longName ||
                      ''}
                  </span>
                </div>

                <div className="ml-auto flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-yellow">
                  <Icon name="east" size={IconSizeEnum.sm} />
                </div>
              </motion.button>

              <AnimatePresence mode="wait">
                {mainNavigation.length + 2 === activeCtaTabIndex && (
                  <MobileLanguageSelector
                    languageActiveIndex={languageActiveIndex}
                    languageSelector={languageSelector}
                  />
                )}
              </AnimatePresence>
            </>
          )}
        </div>
      </motion.ul>
    </motion.div>
  );
};

export default MobileTopLevel;
